import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Link2, ShieldCheck, Tv, UserPlus, XCircle } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { adminApprovalsApi, type RequestItem, type RequestKind, type RequestState } from "../../api/collab";
import { Badge, Banner, Button, Card, EmptyState, Field, Modal, PageHeader, Segmented, SkeletonRows, Textarea, formatRelative, useToast } from "../../ds";

const KIND: Record<RequestKind, { label: string; icon: typeof Link2 }> = {
  share_link: { label: "Public link", icon: Link2 },
  tv_publish: { label: "TV publish", icon: Tv },
  access: { label: "Access request", icon: UserPlus },
};

type StateFilter = "pending" | "approved" | "rejected" | "all";

/** IT admin queue: public-link requests, TV publish requests and access requests. */
export function ApprovalsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [state, setState] = useState<StateFilter>("pending");
  const [rejecting, setRejecting] = useState<RequestItem | null>(null);
  const [note, setNote] = useState("");
  const [approving, setApproving] = useState<RequestItem | null>(null);
  const list = useQuery({ queryKey: ["approvals", state], queryFn: () => adminApprovalsApi.list(state as RequestState | "all"), refetchInterval: 30_000 });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["approvals"] });
    qc.invalidateQueries({ queryKey: ["approvals-count"] });
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  const approve = useMutation({
    mutationFn: (r: RequestItem) => adminApprovalsApi.approve(r),
    onSuccess: (r) => {
      setApproving(null);
      toast.success("Approved", r.kind === "tv_publish" ? `Published version ${r.published_version_number ?? ""} and added the screens.` : r.kind === "share_link" ? "The requester can collect the link." : "Access granted.");
      refresh();
    },
    onError: (e) => {
      setApproving(null);
      toast.error("Couldn't approve", e instanceof Error ? e.message : undefined);
      refresh();
    },
  });
  const reject = useMutation({
    mutationFn: (r: RequestItem) => adminApprovalsApi.reject(r, note),
    onSuccess: () => {
      setRejecting(null);
      setNote("");
      toast.success("Rejected", "The requester sees your note.");
      refresh();
    },
    onError: (e) => toast.error("Couldn't reject", e instanceof Error ? e.message : undefined),
  });

  const rows = list.data ?? [];
  return (
    <div className="ds-page">
      <PageHeader title="Approvals" subtitle="Owners ask here before a report is sent outside the company or put on the TV screens. Nothing changes until you approve." />
      <div className="ds-toolbar">
        <Segmented<StateFilter>
          value={state}
          onChange={setState}
          options={[
            { value: "pending", label: "Waiting" },
            { value: "approved", label: "Approved" },
            { value: "rejected", label: "Rejected" },
            { value: "all", label: "All" },
          ]}
        />
      </div>
      {list.isLoading ? (
        <Card><SkeletonRows rows={4} /></Card>
      ) : list.isError ? (
        <Banner tone="danger" title="Couldn't load the queue">{list.error instanceof Error ? list.error.message : null}</Banner>
      ) : rows.length === 0 ? (
        <Card><EmptyState icon={ShieldCheck} title={state === "pending" ? "Nothing is waiting" : "Nothing here"} description={state === "pending" ? "New requests show up here and in the badge on this menu item." : undefined} /></Card>
      ) : (
        <div className="ds-stack">
          {rows.map((r) => {
            const K = KIND[r.kind];
            return (
              <Card key={`${r.kind}:${r.id}`}>
                <div className="ds-row" style={{ gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                  <K.icon size={20} />
                  <div className="ds-grow" style={{ minWidth: 260 }}>
                    <div className="ds-row" style={{ gap: 8, flexWrap: "wrap" }}>
                      <strong>{K.label}</strong>
                      <span>for <Link to={`/admin/reports/${r.report_id}`}>{r.report_name ?? "a report"}</Link></span>
                      <Badge tone={r.status === "approved" ? "success" : r.status === "pending" ? "warning" : "neutral"} dot>{r.status}</Badge>
                      {r.stale ? <Badge tone="danger" title="The owner changed the draft after asking">Draft changed</Badge> : null}
                    </div>
                    <div className="ds-small ds-muted" style={{ marginTop: 4 }}>
                      {r.requested_by_name ?? "Someone"} · {formatRelative(r.created_at)}
                      {r.decided_by_name ? ` · decided by ${r.decided_by_name} ${formatRelative(r.decided_at)}` : ""}
                    </div>
                    <Details r={r} />
                    {r.decision_note ? <div className="ds-small" style={{ marginTop: 4 }}>Note: {r.decision_note}</div> : null}
                  </div>
                  {r.status === "pending" ? (
                    <div className="ds-row" style={{ gap: 8 }}>
                      <Button icon={XCircle} onClick={() => { setNote(""); setRejecting(r); }}>Reject</Button>
                      <Button variant="primary" icon={CheckCircle2} loading={approve.isPending && approve.variables?.id === r.id} onClick={() => (r.kind === "share_link" ? setApproving(r) : approve.mutate(r))}>Approve</Button>
                    </div>
                  ) : null}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title="Reject request"
        description="The requester sees this note, so say what they could do instead."
        footer={<><Button onClick={() => setRejecting(null)}>Cancel</Button><Button variant="danger" icon={XCircle} disabled={note.trim().length < 3} loading={reject.isPending} onClick={() => rejecting && reject.mutate(rejecting)}>Reject</Button></>}
      >
        <Field label="Note"><Textarea value={note} rows={3} maxLength={2000} onChange={(e) => setNote(e.target.value)} autoFocus /></Field>
      </Modal>

      <Modal
        open={!!approving}
        onClose={() => setApproving(null)}
        title="Approve public link?"
        description="Anyone with the link will see this report without signing in, as the requester sees it."
        footer={<><Button onClick={() => setApproving(null)}>Cancel</Button><Button variant="primary" icon={CheckCircle2} loading={approve.isPending} onClick={() => approving && approve.mutate(approving)}>Approve link</Button></>}
      >
        {approving ? <Details r={approving} /> : null}
      </Modal>
    </div>
  );
}

function Details({ r }: { r: RequestItem }) {
  if (r.kind === "share_link") {
    return (
      <div className="ds-small" style={{ marginTop: 6 }}>
        <div><span className="ds-muted">For:</span> {r.recipients}</div>
        <div><span className="ds-muted">Why:</span> {r.reason}</div>
        <div><span className="ds-muted">Expires:</span> {r.expires_in_days ? `after ${r.expires_in_days} days` : "never (until turned off)"}</div>
      </div>
    );
  }
  if (r.kind === "tv_publish") {
    return (
      <div className="ds-small" style={{ marginTop: 6 }}>
        <div><span className="ds-muted">Draft revision:</span> {r.draft_revision}{r.published_version_number ? ` → published as v${r.published_version_number}` : ""}</div>
        <div><span className="ds-muted">Screens:</span> {r.target_names.length ? r.target_names.join(", ") : "none added (a new version for the screens it is already on)"}</div>
        {r.note ? <div><span className="ds-muted">Note:</span> {r.note}</div> : null}
      </div>
    );
  }
  return r.message ? <div className="ds-small" style={{ marginTop: 6 }}><span className="ds-muted">Message:</span> {r.message}</div> : <div className="ds-small ds-muted" style={{ marginTop: 6 }}>Wants to open the report (Viewer access).</div>;
}
